import React, { useMemo } from "react";
import { sharedLib } from "@shared/services/shared.service";
import { groupConfigurationsFilters } from "@shared/slices/filters.slice";
import { useSelector } from "react-redux";
import { skipToken } from "@reduxjs/toolkit/query";
import ConfigurationCard from "features/admin/TradingConfiguration/ConfigurationCard";
import EqualColumnsGrid from "common/Grids/EqualColumnsGrid";
import {
  LEVERAGE_EXCHANGES_TAG,
  NON_LEVERAGE_EXCHANGES_TAG,
} from "@shared/common/constants";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { MOBILE_MENU, PAGE_SPINNER_MARGIN_FROM_TOP } from "common/constants";
import { usePageIntro } from "common/NewLook";
import Panel from "common/NewLook/Panel.tsx";
import Skeleton from "common/NewLook/Skeleton.tsx";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import {
  NEW_LOOK_SECTION_GAP,
  phoneMaxWidth,
  NEW_LOOK_PAGE_GUTTER_VAR,
} from "common/NewLook/layout.ts";
import HighlightedText from "common/Text/HighlightedText";
import Text from "common/Typography/Text";
import useMedia from "utils/hooks/useMedia";
import styled, { useTheme } from "styled-components";
import { FiltersWrapper } from "features/admin/group/users/styles.ts";
import GroupSelect from "features/admin/group/GroupSelect.tsx";
import { LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useBottomNavigator } from "utils/hooks/misc.tsx";

const TradingConfiguration = () => {
  const { t, i18n } = useTypedTranslation(LanguageNameSpaces.ADMIN);
  const theme = useTheme();
  const numColumns = useMedia([`(max-width: 900px)`], [1], 2);
  const isMobileMenu = useMedia([`(max-width: ${MOBILE_MENU})`], [true], false);
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const { data } = sharedLib.sharedApi.admin.useGetAdminSubGroupsQuery();
  const [subGroupId] = useSelector(
    groupConfigurationsFilters.selectors.selectGroupsFilter
  );
  const subGroup = useMemo(() => {
    return data?.find((group) => group.id === subGroupId);
  }, [data, subGroupId]);
  const {
    currentData: spotData,
    isLoading: isLoadingSpot,
    isFetching: isFetchingSpot,
  } = sharedLib.sharedApi.bots.useGetSubGroupDefaultConfigurationQuery(
    subGroupId
      ? { sub_group_id: subGroupId, exchange: NON_LEVERAGE_EXCHANGES_TAG }
      : skipToken
  );
  const {
    currentData: leverageData,
    isLoading: isLoadingLeverage,
    isFetching: isFetchingLeverage,
  } = sharedLib.sharedApi.bots.useGetSubGroupDefaultConfigurationQuery(
    subGroupId
      ? { sub_group_id: subGroupId, exchange: LEVERAGE_EXCHANGES_TAG }
      : skipToken
  );

  const isLoading =
    isLoadingSpot || isLoadingLeverage || !leverageData || !spotData;
  const isFetching = isFetchingSpot || isFetchingLeverage;

  // New look: the explanation becomes the page description.
  usePageIntro(
    theme.isNewLook
      ? { description: t("tradingConfiguration.description") }
      : {},
    [theme.isNewLook, i18n.language]
  );

  // New look: the two configurations side by side as panels; while they
  // load, panels shaped like them (no spinner over an empty page).
  if (theme.isNewLook) {
    const skeletonPanel = (key: string) => (
      <Panel key={key} title={<Skeleton width={140} height={14} />}>
        {[0, 1, 2].map((group) => (
          <SkeletonGroup key={group}>
            <Skeleton width={64} height={10} />
            {[0, 1].map((row) => (
              <SkeletonRow key={row}>
                <Skeleton width={110} height={12} />
                <Skeleton width={80} height={12} />
              </SkeletonRow>
            ))}
          </SkeletonGroup>
        ))}
      </Panel>
    );
    return (
      <NewLookWrapper>
        {/* The header already shows the group picker on wider screens. */}
        {isMobileMenu && <GroupSelect size="middle" />}
        {isBottomNavigator && (
          <NewLookMobileDescription>
            {t("tradingConfiguration.description")}
          </NewLookMobileDescription>
        )}
        <NewLookGrid aria-busy={isLoading || isFetching}>
          {isLoading ? (
            <>
              {skeletonPanel("leverage")}
              {skeletonPanel("spot")}
            </>
          ) : (
            <>
              {leverageData && (
                <ConfigurationCard
                  botData={leverageData}
                  isLeverage={true}
                  subGroup={subGroup}
                />
              )}
              {spotData && (
                <ConfigurationCard botData={spotData} subGroup={subGroup} />
              )}
            </>
          )}
        </NewLookGrid>
      </NewLookWrapper>
    );
  }

  return (
    <>
      <FiltersWrapper $addTopBorder={true}>
        <GroupSelect size="middle" />
      </FiltersWrapper>
      <Wrapper>
        <LoadingSpinnerOverlay
          isLoading={isLoading || isFetching}
          hideContentWhileLoading={isLoading}
          spinnerSize="large"
          backgroundColor={theme.colors.layoutBgColor}
          spinnerMarginTop={PAGE_SPINNER_MARGIN_FROM_TOP}
        >
          <HighlightedText
            style={{ marginBottom: 16, width: "100%" }}
            bgColor={theme.colors.statisticsBgColor}
          >
            <Text>{t("tradingConfiguration.description")}</Text>
          </HighlightedText>
          <EqualColumnsGrid numColumns={numColumns}>
            {leverageData && (
              <ConfigurationCard
                botData={leverageData}
                isLeverage={true}
                subGroup={subGroup}
              />
            )}
            {spotData && (
              <ConfigurationCard botData={spotData} subGroup={subGroup} />
            )}
          </EqualColumnsGrid>
        </LoadingSpinnerOverlay>
      </Wrapper>
    </>
  );
};

export default TradingConfiguration;

const NewLookWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${NEW_LOOK_SECTION_GAP}px;
  padding: 16px ${NEW_LOOK_PAGE_GUTTER_VAR} ${NEW_LOOK_PAGE_GUTTER_VAR};
  @media (max-width: ${phoneMaxWidth}) {
    gap: 12px;
    padding: 12px 16px 16px;
  }
`;
const NewLookMobileDescription = styled.p`
  margin: 0;
  ${newLookType.body};
  font-size: 13px;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
// Two panels side by side (one column on narrow screens), never stretched
// into wide empty tracks.
const NewLookGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${NEW_LOOK_SECTION_GAP}px;
  align-items: start;
  max-width: 1280px;
  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
const SkeletonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  &:not(:last-child) {
    margin-bottom: 16px;
    padding-bottom: 16px;
    border-bottom: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
`;
const SkeletonRow = styled.div`
  display: flex;
  justify-content: space-between;
`;
const Wrapper = styled.div`
  padding: 20px;
  max-width: 900px;
  display: flex;
  align-items: center;
  flex-direction: column;
  margin: 0 auto;
  @media (max-width: ${phoneMaxWidth}) {
    padding: 12px;
  }
`;
